import { z } from "zod"

import {
	Button,
	Dialog,
	Form,
	FormInput,
	FormSelect,
	FormTextarea,
	Loader,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

interface CancelSubscriptionDialogProps {
	isOpen: boolean
	onClose: () => void
}

const cancellationReasons = [
	{ label: "Some features are missing", value: "Some features are missing" },
	{
		label: "Too hard or complex to use",
		value: "Too hard or complex to use",
	},
	{
		label: "Too many performance issues or bugs",
		value: "Too many performance issues or bugs",
	},
	{
		label: "Customer service was less than expected",
		value: "Customer service was less than expected",
	},
	{ label: "It's too expensive", value: "It's too expensive" },
	{
		label: "We don't need it anymore",
		value: "We don't need it anymore",
	},
	{ label: "Other", value: "Other" },
]

const cancelFormSchema = z.object({
	reason: z.string().min(1, "Please select a reason"),
	elaboration: z
		.string()
		.min(1, "Please provide some details, it helps us improve!"),
	competitor: z.string().min(1, "Your input here would really help us."),
})

export function CancelSubscriptionDialog({
	isOpen,
	onClose,
}: CancelSubscriptionDialogProps) {
	const form = useZodForm({
		schema: cancelFormSchema,
	})

	const utils = api.useUtils()
	const { mutateAsync, isLoading } =
		api.workspace.cancelSubscription.useMutation()

	const onSubmit = async (values: z.infer<typeof cancelFormSchema>) => {
		try {
			await mutateAsync(values)
			void utils.workspace.trialInfo.invalidate()
			toast.success({
				title: "Subscription cancelled",
				description:
					"Your subscription will remain active until the end of your billing period.",
			})
			form.reset()
			onClose()
		} catch {
			toast.error({
				title: "Failed to cancel subscription",
				description: "Please contact our support team at hello@productlane.com",
			})
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={(value) => {
				if (!value) onClose()
			}}
			title="Are you sure you want to cancel your plan?"
			footer={
				<div className="flex w-full items-center justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="danger"
						form="cancel-subscription-form"
						type="submit"
						disabled={isLoading}
					>
						Confirm cancellation
					</Button>
				</div>
			}
		>
			{isLoading && (
				<div className="absolute inset-0 z-50 flex items-center justify-center gap-4 rounded-lg backdrop-blur">
					<Loader />
					<span className="pl-2">Cancelling subscription</span>
				</div>
			)}

			<Form
				id="cancel-subscription-form"
				form={form}
				onSubmit={onSubmit}
				className="space-y-3"
			>
				<FormSelect
					name="reason"
					label="Why are you leaving Productlane?"
					placeholder="Choose"
					options={cancellationReasons}
				/>
				<FormTextarea
					name="elaboration"
					placeholder="Can you elaborate? How could we have improved?"
					minRows={3}
				/>
				<FormInput
					name="competitor"
					placeholder="What tool are you switching to?"
				/>
			</Form>
		</Dialog>
	)
}
